import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import StarRating from './StarRating';
import MokaPotBadge from './MokaPotBadge';
import { User, Heart, Pencil, Trash2, Languages, Loader2, ImageIcon } from 'lucide-react';
import { api } from '@/api/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export default function ReviewCard({ review, user, initialLikeCount, isOwner, onEdit = null, onDelete = null, showTranslateUI = false, reviewerReviewCount, reviewerAvatarUrl, linkToProfile = true }) {
  const [likeCount, setLikeCount] = useState(initialLikeCount ?? 0);
  const [isLiked, setIsLiked] = useState(false);
  const [likeId, setLikeId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const date = review.created_date
    ? format(new Date(review.created_date), 'MMM d, yyyy')
    : '';

  useEffect(() => {
    // If initialLikeCount was provided, only fetch user's own like status
    if (initialLikeCount !== undefined) {
      if (user) {
        api.entities.ReviewLike.filter({ review_id: review.id, user_id: user.id }).then((likes) => {
          if (likes.length > 0) { setIsLiked(true); setLikeId(likes[0].id); }
        });
      }
    } else {
      api.entities.ReviewLike.filter({ review_id: review.id }).then((likes) => {
        setLikeCount(likes.length);
        if (user) {
          const myLike = likes.find((l) => l.user_id === user.id);
          if (myLike) { setIsLiked(true); setLikeId(myLike.id); }
        }
      });
    }
  }, [review.id, user?.id]);

  const handleLike = async () => {
    if (!user) {
      api.auth.redirectToLogin(window.location.pathname);
      return;
    }
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      if (isLiked) {
        await api.entities.ReviewLike.delete(likeId);
        setIsLiked(false);
        setLikeId(null);
      } else {
        const newLike = await api.entities.ReviewLike.create({ review_id: review.id, user_id: user.id });
        setIsLiked(true);
        setLikeId(newLike.id);
      }
      const currentLikes = await api.entities.ReviewLike.filter({ review_id: review.id });
      setLikeCount(currentLikes.length);
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Translation ---
  const ALL_LANG_OPTIONS = [
    { code: 'EN', label: 'English' },
    { code: 'JA', label: '日本語' },
    { code: 'TH', label: 'ไทย' },
    { code: 'ZH', label: '中文' },
  ];

  const getDefaultLang = () => {
    const lang = navigator.language?.toLowerCase() || 'en';
    return lang.startsWith('ja') ? 'JA' : lang.startsWith('th') ? 'TH' : 'EN';
  };

  const [expanded, setExpanded] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  const [targetLang, setTargetLang] = useState(getDefaultLang());
  const [translating, setTranslating] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [showTranslation, setShowTranslation] = useState(false);
  const [translationError, setTranslationError] = useState('');
  const handleTranslate = async () => {
    if (!user) {
      api.auth.redirectToLogin(window.location.pathname);
      return;
    }
    setTranslationError('');
    setTranslating(true);

    try {
      const res = await api.functions.invoke('translateReview', {
        review_id: review.id,
        target_lang: targetLang,
      });
      const data = res.data;
      setTranslatedText(data.translated_text || '');
      setShowTranslation(true);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setTranslationError(msg || 'Translation failed. Please try again later.');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-4 flex flex-col h-full cursor-pointer" onClick={() => setExpanded((v) => !v)}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <ReviewerIdentity
          reviewerId={linkToProfile ? review.reviewer_id : null}
          name={review.reviewer_name}
          avatarUrl={reviewerAvatarUrl}
          reviewCount={reviewerReviewCount}
          date={date}
        />
        <div className="flex items-center gap-2">
          <StarRating rating={review.rating} size={14} />
          {isOwner && (
            <div className="flex gap-1">
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="p-1.5 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                >
                  <Pencil size={14} />
                </button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="p-1.5 hover:bg-destructive/10 rounded-lg transition-colors text-muted-foreground hover:text-destructive">
                    <Trash2 size={14} />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogTitle>Delete Review</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this review? This action cannot be undone.
                  </AlertDialogDescription>
                  <div className="flex flex-row justify-end gap-2 mt-2">
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  </div>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="space-y-2 mt-3 flex-1">
        {review.comment && !showTranslation && (
          <p className={`text-sm text-foreground/80 leading-relaxed ${expanded ? '' : 'line-clamp-3'}`}>{review.comment}</p>
        )}


        {showTranslateUI && review.comment && (
          <div className="pt-1 space-y-2">
            {!showTranslation ? (
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground font-medium">Translate to</span>
                <select
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[11px] bg-secondary/50 border border-border rounded-lg px-2 py-1 font-medium text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {ALL_LANG_OPTIONS.map((opt) => (
                    <option key={opt.code} value={opt.code}>{opt.label}</option>
                  ))}
                </select>
                <button
                  onClick={(e) => { e.stopPropagation(); handleTranslate(); }}
                  disabled={translating}
                  className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {translating ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
                  {translating ? 'Translating...' : 'Translate'}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {translatedText && (
                  <p className="text-sm text-foreground/80 leading-relaxed">{translatedText}</p>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setShowTranslation(false); }}
                  className="text-[10px] text-primary font-medium hover:underline"
                >
                  Show original
                </button>
              </div>
            )}
            {translationError && (
              <p className="text-[10px] text-destructive leading-tight">{translationError}</p>
            )}
          </div>
        )}
      </div>

      {/* Photo toggle */}
      {review.photo_url && (
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowPhoto((v) => !v)}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-lg bg-secondary text-muted-foreground hover:text-foreground font-medium transition-colors"
          >
            <ImageIcon size={12} />
            {showPhoto ? 'Hide photo' : 'View photo'}
          </button>
          {showPhoto && (
            <img
              src={review.photo_url}
              alt="Review photo"
              className="mt-2 rounded-xl object-contain max-h-56 max-w-[280px]"
            />
          )}
        </div>
      )}

      {/* Like button */}
      <div className="flex items-center justify-end pt-2 border-t border-border/50 mt-3">
        <button
          onClick={(e) => { e.stopPropagation(); handleLike(); }}
          disabled={isProcessing}
          className={`flex items-center gap-1.5 text-xs transition-all active:scale-90 disabled:opacity-60 ${
            isLiked ? 'text-rose-500' : 'text-muted-foreground hover:text-rose-400'
          }`}
        >
          <Heart size={14} className={isLiked ? 'fill-rose-500' : ''} />
          <span>{likeCount > 0 ? likeCount : ''}</span>
        </button>
      </div>
    </div>
  );
}

// The reviewer's avatar and name, linked to their profile when the review
// carries a reviewer_id. The card itself toggles expansion on click, so the
// link has to keep its own click to itself.
function ReviewerIdentity({ reviewerId, name, avatarUrl, reviewCount, date }) {
  const body = (
    <>
      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
        {avatarUrl ? (
          <img src={avatarUrl} alt={name || 'avatar'} className="w-full h-full object-cover" />
        ) : (
          <User size={16} className="text-muted-foreground" strokeWidth={1.5} />
        )}
      </div>
      <div className="min-w-0">
        <p className={`text-sm font-semibold text-foreground flex items-center gap-1.5 ${reviewerId ? 'group-hover:underline' : ''}`}>
          <span className="truncate">{name || 'Anonymous'}</span>
          {reviewCount != null && <MokaPotBadge reviewCount={reviewCount} size={16} />}
        </p>
        <p className="text-xs text-muted-foreground">{date}</p>
      </div>
    </>
  );

  if (!reviewerId) {
    return <div className="flex items-center gap-2 min-w-0">{body}</div>;
  }

  return (
    <Link
      to={`/user/${reviewerId}`}
      onClick={(e) => e.stopPropagation()}
      title={`View ${name || 'this member'}'s profile`}
      className="group flex items-center gap-2 min-w-0 rounded-lg -m-1 p-1 hover:bg-secondary/60 transition-colors"
    >
      {body}
    </Link>
  );
}
